/**
 * Normalization of product hits between the v2 and v3 Algolia index formats.
 *
 * - **v2**: 1 document = 1 product; variants nested in `variants[]`; product id carried
 *   by `_id` (= `objectID`).
 * - **v3**: 1 document = 1 flattened variant (no more `variants[]`); variant attributes at
 *   the root (`variant_id`, `variant_name`); product id in `product_id` (the `objectID` is
 *   the composite `productId__variantId`). In a standard search, a `distinct` on `product_id`
 *   returns a single "champion" variant per product.
 *
 * These helpers let the surfaces read a hit without knowing the underlying format.
 */

export type ProductHitVariant = { id: string; name?: string; [key: string]: unknown }

export type ProductHit = {
  _id?: string
  objectID?: string
  product_id?: string
  variant_id?: string
  variant_name?: string
  name?: string
  variants?: ProductHitVariant[] | null
  [key: string]: unknown
}

/** Product id of the hit, whatever the format: v3 `product_id`, otherwise v2 `_id`/`objectID`. */
export function getProductId(hit: ProductHit): string | undefined {
  return hit.product_id ?? hit._id ?? hit.objectID
}

/**
 * Variants of the hit, normalized to an array:
 * - **v2**: returns the nested `variants[]` array as-is;
 * - **v3**: rebuilds a single-element array from the flattened variant attributes
 *   (`variant_id`/`variant_name`); `[]` if the hit carries no variant.
 */
export function getProductVariants(hit: ProductHit): ProductHitVariant[] {
  if (Array.isArray(hit.variants)) return hit.variants
  if (hit.variant_id == null) return []
  return [{ id: hit.variant_id, name: hit.variant_name ?? hit.name }]
}
